import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { getAuthHeaders } from "./authService";

const AuthContext = createContext(null);

const STORAGE_KEY = "cumalo_user";
const FAVORITES_LIMIT = parseInt(process.env.REACT_APP_FAVORITES_LIMIT || "20", 10);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [favorites, setFavorites] = useState([]);
  const [pendingAdd, setPendingAdd] = useState(new Set());
  const [pendingRemove, setPendingRemove] = useState(new Set());
  const [favoritesError, setFavoritesError] = useState(null);

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const favoriteNames = useMemo(() => {
    const names = new Set([
      ...favorites.map((f) => f.name),
      ...pendingAdd,
    ]);
    pendingRemove.forEach((n) => names.delete(n));
    return names;
  }, [favorites, pendingAdd, pendingRemove]);

  const atFavoritesLimit = useMemo(
    () => favorites.length + pendingAdd.size - pendingRemove.size >= FAVORITES_LIMIT,
    [favorites, pendingAdd, pendingRemove]
  );

  const loadFavorites = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/favorites", { headers });
      if (!res.ok) return;
      const data = await res.json();
      setFavorites(data);
    } catch {
      // silently fail — favorites will just be empty
    }
  }, []);

  const addFavorite = useCallback(async (name, meaning, theme) => {
    if (favoriteNames.has(name) || atFavoritesLimit) return;

    setPendingAdd((prev) => new Set(prev).add(name));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/favorites", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ name, meaning, theme: theme === "general" ? null : theme }),
      });
      if (!res.ok) {
        throw new Error(await res.text());
      }
      const saved = await res.json();
      if (saved) {
        setFavorites((prev) => [saved, ...prev]);
      }
    } catch {
      setFavoritesError("favoriteAddError");
    } finally {
      setPendingAdd((prev) => {
        const s = new Set(prev);
        s.delete(name);
        return s;
      });
    }
  }, [favoriteNames, atFavoritesLimit]);

  const removeFavorite = useCallback(async (name) => {
    setPendingRemove((prev) => new Set(prev).add(name));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/favorites/${encodeURIComponent(name)}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok && res.status !== 404) {
        throw new Error();
      }
      setFavorites((prev) => prev.filter((f) => f.name !== name));
    } catch {
      setFavoritesError("favoriteRemoveError");
    } finally {
      setPendingRemove((prev) => {
        const s = new Set(prev);
        s.delete(name);
        return s;
      });
    }
  }, []);

  const login = (userData) => {
    setUser(userData);
    Sentry.setUser({ id: userData.uid }); // Firebase UID only — no email
  };

  const logout = () => {
    setUser(null);
    setFavorites([]);
    setPendingAdd(new Set());
    setPendingRemove(new Set());
    Sentry.setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, login, logout,
      favorites, favoriteNames, favoritesLimit: FAVORITES_LIMIT, atFavoritesLimit,
      addFavorite, removeFavorite, loadFavorites,
      favoritesError, clearFavoritesError: () => setFavoritesError(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
